import { useEffect } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  /** Key into the `seo` namespace, e.g. "home", "about", "contact". */
  page: "home" | "services" | "about" | "strategic" | "logistics" | "contact";
};

function setMeta(selector: string, attr: "name" | "property", key: string, content: string) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Updates document.title and SEO meta tags whenever the i18n language changes.
 * The route's static `head()` still provides SSR/initial defaults; this hook
 * keeps tags in sync once the user toggles EN/IT in the client.
 */
export function SeoHead({ page }: Props) {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const title = t(`seo.${page}.title`);
    const description = t(`seo.${page}.description`);
    const ogTitle = t(`seo.${page}.ogTitle`);
    const ogDescription = t(`seo.${page}.ogDescription`);

    if (typeof document !== "undefined") {
      document.title = title;
      document.documentElement.lang = i18n.language?.startsWith("it") ? "it" : "en";
    }

    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[property="og:title"]', "property", "og:title", ogTitle);
    setMeta('meta[property="og:description"]', "property", "og:description", ogDescription);
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", ogTitle);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", ogDescription);
  }, [t, i18n.language, page]);

  return null;
}